import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { api } from "../../lib/api";
import { formatApiError } from "../../context/AuthContext";
import { SERVICE_OPTIONS, EQUIPMENT_OPTIONS } from "../../data/content";

const field = "w-full bg-[#0B132B] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:outline-none transition-colors";

export const LeadForm = ({ source = "contact", defaultService = "", compact = false, submitLabel = "LET'S TALK TRUCKING" }) => {
  const [form, setForm] = useState({
    full_name: "", company_name: "", phone: "", email: "", usdot: "", mc_number: "",
    num_trucks: "", equipment: "", years_in_business: "", service: defaultService, message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.service) {
      toast.error("Please fill in your name, email, and the service you're interested in.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source });
      setSent(true);
      toast.success("Thank you! We'll be in touch soon.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        className="nm-card rounded-2xl p-10 text-center" data-testid="lead-form-success">
        <div className="w-16 h-16 mx-auto rounded-full nm-gold-bg flex items-center justify-center mb-5">
          <Send className="text-[#0B132B]" />
        </div>
        <h3 className="font-head text-2xl font-bold text-white mb-2">You're on the road with us.</h3>
        <p className="text-gray-400 text-sm">We received your inquiry and will reach out shortly. Your next mile starts here.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="nm-card rounded-2xl p-6 sm:p-8 space-y-4" data-testid="lead-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input className={field} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} data-testid="lead-full-name" />
        <input className={field} placeholder="Company Name" value={form.company_name} onChange={set("company_name")} data-testid="lead-company" />
        <input className={field} placeholder="Phone Number" value={form.phone} onChange={set("phone")} data-testid="lead-phone" />
        <input className={field} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="lead-email" />
        {!compact && <input className={field} placeholder="USDOT Number (optional)" value={form.usdot} onChange={set("usdot")} data-testid="lead-usdot" />}
        {!compact && <input className={field} placeholder="MC Number (optional)" value={form.mc_number} onChange={set("mc_number")} data-testid="lead-mc" />}
        {!compact && <input className={field} placeholder="Number of Trucks" value={form.num_trucks} onChange={set("num_trucks")} data-testid="lead-trucks" />}
        {!compact && (
          <select className={field} value={form.equipment} onChange={set("equipment")} data-testid="lead-equipment">
            <option value="">Equipment Type</option>
            {EQUIPMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
        {!compact && <input className={field} placeholder="Years / Months in Business" value={form.years_in_business} onChange={set("years_in_business")} data-testid="lead-years" />}
        <select className={field} value={form.service} onChange={set("service")} data-testid="lead-service">
          <option value="">Service Interested In *</option>
          {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
      <textarea className={`${field} min-h-[110px] resize-none`} placeholder="Tell us where your business is today and where you're trying to go..."
        value={form.message} onChange={set("message")} data-testid="lead-message" />
      <button type="submit" disabled={loading}
        className="btn-sheen w-full flex items-center justify-center gap-2 nm-gold-bg text-[#0B132B] font-bold py-3.5 rounded-full hover:scale-[1.01] transition-transform disabled:opacity-60"
        data-testid="lead-submit-btn">
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={17} />}
        {loading ? "Sending..." : submitLabel}
      </button>
    </form>
  );
};
